import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Lightbulb, ArrowFatUp, ChatCircle } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [adminResponse, setAdminResponse] = useState("");
  const [releaseVersion, setReleaseVersion] = useState("");

  useEffect(() => {
    fetchIdeas();
  }, [statusFilter]);

  const fetchIdeas = async () => {
    try {
      const url = statusFilter === "all" ? `${API}/admin/ideas` : `${API}/admin/ideas?status=${statusFilter}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setIdeas((await response.json()).ideas);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateIdea = async (status) => {
    try {
      await fetch(`${API}/admin/ideas/${selectedIdea.idea_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ status, admin_response: adminResponse, release_version: releaseVersion })
      });
      toast.success("Idea updated");
      fetchIdeas();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getStatusColor = (status) => {
    const colors = { submitted: "border-zinc-500 text-zinc-400", planned: "border-blue-500 text-blue-400", in_progress: "border-amber-500 text-amber-400", released: "border-emerald-500 text-emerald-400", declined: "border-red-500 text-red-400" };
    return colors[status] || colors.submitted;
  };

  const openDetail = (idea) => {
    setSelectedIdea(idea);
    setAdminResponse(idea.admin_response || "");
    setReleaseVersion(idea.release_version || "");
    setDetailModalOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-ideas">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white uppercase">Enhancement Ideas</h1>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ideas</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="released">Released</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-zinc-800 rounded-sm animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3">
          {ideas.map((idea) => (
            <Card key={idea.idea_id} className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-zinc-700" onClick={() => openDetail(idea)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center justify-center w-14 h-14 bg-zinc-800 rounded-sm">
                    <ArrowFatUp size={20} className="text-[#FF6600]" />
                    <span className="font-bold text-white text-sm">{idea.upvotes}</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{idea.title}</p>
                    <p className="text-sm text-zinc-500">{idea.tenant?.company_name} • {idea.category}</p>
                  </div>
                  <Badge variant="outline" className={getStatusColor(idea.status)}>{idea.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{selectedIdea?.title}</DialogTitle>
          </DialogHeader>
          {selectedIdea && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded-sm">
                  <ArrowFatUp size={20} className="text-[#FF6600]" />
                  <span className="font-bold">{selectedIdea.upvotes} votes</span>
                </div>
                <Badge variant="secondary">{selectedIdea.category}</Badge>
              </div>
              <div className="p-4 bg-zinc-800 rounded-sm">
                <p className="text-sm text-zinc-300">{selectedIdea.description}</p>
                <p className="text-xs text-zinc-500 mt-2">{selectedIdea.user_name} • {new Date(selectedIdea.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-zinc-400">Status</Label>
                <Select value={selectedIdea.status} onValueChange={(v) => setSelectedIdea({ ...selectedIdea, status: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="released">Released</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Admin Response (public)</Label>
                <Textarea value={adminResponse} onChange={(e) => setAdminResponse(e.target.value)} placeholder="Your response to this idea..." className="mt-1 bg-zinc-800 border-zinc-700 text-white" rows={3} />
              </div>
              <div>
                <Label className="text-zinc-400">Release Version (if released)</Label>
                <Input value={releaseVersion} onChange={(e) => setReleaseVersion(e.target.value)} placeholder="v1.2.0" className="mt-1 bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <Button onClick={() => updateIdea(selectedIdea.status)} className="w-full bg-[#FF6600] hover:bg-[#CC5200] text-white">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
